<script setup lang="ts">
import { ConsentBanner } from '@/components'
import { RouterView } from 'vue-router'
</script>

<template>
  <RouterView />
  <ConsentBanner />
</template>

<style>
/* Outfit, self-hosted to match yourstrategic.com. Only 400 and 700 exist on the
   brand site, so no other weight is referenced anywhere in this project. */
@font-face {
  font-display: swap;
  font-family: 'Outfit';
  font-style: normal;
  font-weight: 400;
  src: url('/fonts/outfit-v11-latin-regular.woff2') format('woff2');
}

@font-face {
  font-display: swap;
  font-family: 'Outfit';
  font-style: normal;
  font-weight: 700;
  src: url('/fonts/outfit-v11-latin-700.woff2') format('woff2');
}

:root {
  /* Palette lifted from yourstrategic.com's app.css */
  --color-charcoal: #3a3a3c;
  --color-blue: #2466ad;
  --color-blue-bright: #3aa5da;
  --color-teal: #6ec8b9;
  --color-pale-blue: #b1d1e1;

  /* Navy for the footer, per the wireframe. Derived from --color-blue so the
     two read as the same family. */
  --color-navy: #14365c;

  --color-text: var(--color-charcoal);
  --color-text-muted: #707070;
  --color-link: var(--color-blue);
  --color-bg: #ffffff;
  --color-bg-alt: #f5f7f9;
  --color-border: #e1e5eb;

  --font-sans: 'Outfit', system-ui, -apple-system, 'Segoe UI', sans-serif;

  /* Type scale mirrors the brand site's ratios (3 / 2.35 / 1.35 / 1.15rem),
     fluid at the top end so the hero does not overflow small screens. */
  --text-h1: clamp(2.25rem, 1.5rem + 3vw, 3rem);
  --text-h2: clamp(1.75rem, 1.35rem + 1.6vw, 2.35rem);
  --text-h3: 1.35rem;
  --text-body: 1.0625rem;
  --text-sm: 0.9375rem;
  --text-xs: 0.8125rem;

  --container: 78rem;
  --container-narrow: 62rem;
  --gutter: clamp(1.25rem, 4vw, 3rem);
  --section-y: clamp(3.5rem, 7vw, 6rem);

  /* The brand site's card language: a 32px radius with a thin charcoal
     outline, white fill, and no shadow. 32px is its dominant radius by a wide
     margin. Its border weight scales with component size: 3px on full-width
     index tiles, 1.5px on .core__card, 1px on .core__module-tile and
     .core__accordion-item. Everything here is small and dense, so it takes the
     1px floor of that range - going thinner would leave their system. */
  --radius-card: 32px;
  --radius-sm: 4px;
  --border-card: 1px solid var(--color-charcoal);
  --border-panel: 1px solid var(--color-charcoal);
  /* #3a3a3c2b - the charcoal veil the site fades in on card and button hover */
  --veil-dark: rgba(58, 58, 60, 0.17);
  --veil-light: rgba(255, 255, 255, 0.35);
  --shadow-sm: 0 1px 2px rgba(58, 58, 60, 0.08);
  --transition: 0.2s ease;

  /* Clears the sticky header when an anchor is jumped to. */
  --header-h: 5.5rem;
}

*,
*::before,
*::after {
  box-sizing: border-box;
}

html {
  scroll-behavior: smooth;
  scroll-padding-top: calc(var(--header-h) + 1.5rem);
}

@media (prefers-reduced-motion: reduce) {
  html {
    scroll-behavior: auto;
  }
}

body {
  margin: 0;
  background-color: var(--color-bg);
  color: var(--color-text);
  font-family: var(--font-sans);
  font-size: var(--text-body);
  font-weight: 400;
  line-height: 1.6;
  -webkit-font-smoothing: antialiased;
  text-rendering: optimizeLegibility;
}

h1,
h2,
h3,
h4 {
  color: var(--color-text);
  font-family: var(--font-sans);
  font-weight: 700;
  line-height: 1.15;
  margin: 0;
  text-wrap: balance;
}

h1 {
  font-size: var(--text-h1);
  letter-spacing: -0.02em;
}

h2 {
  font-size: var(--text-h2);
  letter-spacing: -0.015em;
}

h3 {
  font-size: var(--text-h3);
}

p {
  margin: 0 0 1.25rem;
  text-wrap: pretty;
}

p:last-child {
  margin-bottom: 0;
}

a {
  color: var(--color-link);
  text-decoration: none;
  transition: color var(--transition);
}

a:hover,
a:focus-visible {
  color: var(--color-teal);
}

:focus-visible {
  outline: 3px solid var(--color-blue-bright);
  outline-offset: 2px;
}

img {
  max-width: 100%;
  height: auto;
}

/* Shared layout primitives ------------------------------------------------ */

.container {
  width: 100%;
  max-width: var(--container);
  margin-inline: auto;
  padding-inline: var(--gutter);
}

.section {
  padding-block: var(--section-y);
}

.section--alt {
  background-color: var(--color-bg-alt);
}

.eyebrow {
  color: var(--color-blue);
  font-size: var(--text-xs);
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

/* Buttons follow the brand site's .core__input__* pills: 32px radius, .2rem
   outline, white fill with charcoal border and text, hovering to a charcoal
   veil. The site pads the right side to 4rem to clear an arrow glyph; with no
   arrow here the padding stays symmetric. Its border is .2rem, which lands at
   ~2.7px against its 85% root font-size; this page runs a 100% root, so the
   border is set in px to match what their site actually renders. */
.btn {
  display: inline-flex;
  align-items: center;
  gap: 0.625rem;
  padding: 0.65rem 2rem;
  border: 2px solid var(--color-charcoal);
  border-radius: var(--radius-card);
  background-color: #fff;
  color: var(--color-charcoal);
  font-family: var(--font-sans);
  font-size: 1.1rem;
  font-weight: 700;
  line-height: 1.3;
  text-align: center;
  cursor: pointer;
  transition:
    background-color var(--transition),
    border-color var(--transition),
    color var(--transition);
}

.btn:hover,
.btn:focus-visible {
  background-color: var(--veil-dark);
  color: var(--color-charcoal);
}

/* On dark backgrounds, matching .core__input__ghost-white. */
.btn--ghost {
  background-color: transparent;
  border-color: #fff;
  color: #fff;
}

.btn--ghost:hover,
.btn--ghost:focus-visible {
  background-color: var(--veil-light);
  border-color: #fff;
  color: #fff;
}

.skip-link {
  position: absolute;
  left: -9999px;
  top: 0;
  z-index: 100;
  padding: 0.75rem 1.25rem;
  background: var(--color-blue);
  color: #fff;
  font-weight: 700;
}

.skip-link:focus {
  left: 0;
  color: #fff;
}
</style>
